import {merge, Observable} from 'rxjs';
import {filter, map, scan} from 'rxjs/operators';
import {logout$} from '@/user/auth';

interface Orders<T> {
  fills: T[];
}

interface OrderRawBase {
  execID: string;
}

type Cache<Raw> = Map<string, Raw>;
type Action<Raw> = (map: Cache<Raw>) => Cache<Raw>;

export function createFillOrdersCache$<OrderRaw extends OrderRawBase>(
  snapshotOrders$: Observable<Orders<OrderRaw | unknown>>,
  incrementalOrders$: Observable<Orders<OrderRaw | unknown>>
) {
  const resetAction$ = logout$.pipe(
    map(
      (): Action<OrderRaw> => (cache: Cache<OrderRaw>) => {
        cache.clear();
        return cache;
      }
    )
  );
  const snapshotAction$ = snapshotOrders$.pipe(
    map((o: Orders<OrderRaw>) => o.fills),
    map((orders: OrderRaw[]): Action<OrderRaw> => {
      return function (cache: Cache<OrderRaw>) {
        cache.clear();
        orders.forEach((o: OrderRaw) => cache.set(o.execID, o));
        return cache;
      };
    })
  );

  const updateAction$ = incrementalOrders$.pipe(
    map((o: Orders<OrderRaw>) => o.fills),
    filter((orders: OrderRaw[]) => orders.length > 0),
    map((orders: OrderRaw[]): Action<OrderRaw> => {
      return function (cache: Cache<OrderRaw>) {
        orders.forEach((o: OrderRaw) => cache.set(o.execID, o));
        return cache;
      };
    })
  );

  return merge(snapshotAction$, updateAction$, resetAction$).pipe(
    scan((p: Cache<OrderRaw>, c: Action<OrderRaw>): Cache<OrderRaw> => c(p), new Map<string, OrderRaw>())
  );
}
